import React, { useState, useEffect } from 'react';
import api from '../utils/apiClient';
import { API_ENDPOINTS, API_BASE_URL } from '../config/api';
import { toast } from 'react-hot-toast';
import { FaUser, FaEnvelope, FaCheckCircle, FaSync } from 'react-icons/fa';
import { useAccount, useWalletClient } from 'wagmi';
import { cacheSignature } from '../utils/signatureCache';

const UsernameSetupModal = ({ isOpen, onClose, userAddress, onProfileSet }) => {
  const [formData, setFormData] = useState({
    username: '',
    email: ''
  });
  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [step, setStep] = useState(1); // 1 = form, 2 = success
  const [isRefreshingToken, setIsRefreshingToken] = useState(false);

  // Get wallet client for signing messages
  const { address } = useAccount();
  const { data: walletClient } = useWalletClient();

  // Pre-fill username field with a suggested username based on wallet address
  useEffect(() => {
    if (isOpen && userAddress) {
      // Generate a suggested username from the wallet address
      const suggestedUsername = `user_${userAddress.substring(2, 8)}`;
      setFormData(prev => ({
        ...prev,
        username: suggestedUsername
      }));
      // Reset step to form view when modal opens
      setStep(1);
    }
  }, [isOpen, userAddress]);

  // Don't render anything if modal is not open
  if (!isOpen) return null;

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Function to refresh the authentication token
  const refreshToken = async () => {
    if (!walletClient || !address) {
      console.error('Wallet client not available for token refresh');
      return false;
    }

    try {
      setIsRefreshingToken(true);

      // Clear all authentication data
      localStorage.removeItem('token');
      localStorage.removeItem('user');

      // Wait a moment for the cleanup to complete
      await new Promise(resolve => setTimeout(resolve, 500));

      // Force a page reload to re-authenticate
      window.location.reload();

      // This will never be reached due to the page reload
      return true;
    } catch (error) {
      console.error('Error refreshing token:', error);
      return false;
    } finally {
      setIsRefreshingToken(false);
    }
  };

  const validateForm = () => {
    const newErrors = {};

    // Validate username
    if (!formData.username.trim()) {
      newErrors.username = 'Username cannot be empty';
    } else if (formData.username.length < 3) {
      newErrors.username = 'Username must be at least 3 characters';
    } else if (formData.username.length > 20) {
      newErrors.username = 'Username must be at most 20 characters';
    } else if (!/^[a-zA-Z0-9_-]+$/.test(formData.username)) {
      newErrors.username = 'Username can only contain letters, numbers, hyphens, and underscores';
    }

    // Validate email - only if provided (email is optional)
    if (formData.email.trim() && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsLoading(true);
    setErrors({}); // Clear previous errors

    // Prepare the data - ensure email is only sent if it's not empty
    const username = formData.username.trim();
    const email = formData.email.trim() || undefined;

    // Log the request for debugging
    console.log('Submitting profile update:', {
      address: userAddress,
      username,
      email
    });

    // Log API configuration for debugging
    console.log('API Configuration:', {
      API_BASE_URL,
      directUpdateProfileEndpoint: API_ENDPOINTS.wallet.directUpdateProfile,
      updateProfileEndpoint: API_ENDPOINTS.wallet.updateProfile,
      fullDirectUrl: `${API_BASE_URL}${API_ENDPOINTS.wallet.directUpdateProfile}`,
      fullUpdateProfileUrl: `${API_BASE_URL}${API_ENDPOINTS.wallet.updateProfile}`
    });

    try {
      // Make sure we have a valid address
      if (!userAddress) {
        throw new Error('Wallet address is missing');
      }

      // Make sure the address is properly formatted
      const formattedAddress = userAddress ? userAddress.toLowerCase() : '';

      // First check if the user exists in the database
      try {
        console.log('Checking if user exists...');
        const userCheckResponse = await api.get(API_ENDPOINTS.wallet.get(formattedAddress));

        if (!userCheckResponse) {
          console.error('User not found in database');
          throw {
            status: 404,
            code: 'USER_NOT_FOUND',
            message: 'User not found in database. Please make sure your wallet is connected properly.'
          };
        }

        console.log('User found:', userCheckResponse);
      } catch (userCheckError) {
        console.error('Error checking if user exists:', userCheckError);
        // Continue anyway, as the user might exist but the API call failed
      }

      // Try to create or update the user profile
      try {
        console.log('Using the create-or-update endpoint...');

        // Use the new endpoint that creates a user if they don't exist
        const response = await api.post(API_ENDPOINTS.users.createOrUpdate, {
          address: formattedAddress,
          username,
          email
        });

        console.log('Create or update response:', response);

        // Check if the request was successful
        if (response && response.success) {
          handleSuccessResponse(response, username, email);
          return;
        } else {
          throw new Error(response.error || 'Failed to create or update user');
        }
      } catch (apiError) {
        console.error('API approach failed:', apiError);

        // Try with fetch as a fallback
        try {
          console.log('Trying with fetch as a fallback...');

          // Make a direct fetch request to the create-or-update endpoint
          const fetchResponse = await fetch(`${API_BASE_URL}/api/user/create-or-update`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              address: formattedAddress,
              username,
              email
            })
          });

          // Parse the response
          const data = await fetchResponse.json();
          console.log('Fetch response:', data);

          // Check if the request was successful
          if (fetchResponse.ok && data.success) {
            handleSuccessResponse(data, username, email);
            return;
          } else {
            throw new Error(data.error || 'Failed to create or update user');
          }
        } catch (fetchError) {
          console.error('Fetch approach failed:', fetchError);
          throw fetchError; // Re-throw to be caught by the outer catch block
        }
      }

      // If we get here, both approaches failed
      const errorDetails = {
        message: 'Failed to create or update user profile',
        apiBaseUrl: API_BASE_URL,
        endpoint: API_ENDPOINTS.users.createOrUpdate,
        userAddress: formattedAddress
      };
      console.error('All profile update approaches failed:', errorDetails);
      throw new Error('Failed to create or update user profile');
    } catch (error) {
      handleErrorResponse(error);
    } finally {
      setIsLoading(false);
    }
  };

  // Helper function to handle successful response
  const handleSuccessResponse = (response, username, email) => {
    // Mark that we've shown the modal for this user
    if (userAddress) {
      localStorage.setItem(`username_modal_shown_${userAddress}`, 'true');
    }

    // Update the user context with the new profile data
    const updatedProfile = {
      username: response.user?.username || username,
      email: response.user?.email || email
    };

    console.log('Updating user profile with:', updatedProfile);

    // Update local storage user data
    try {
      const userData = localStorage.getItem('user');
      if (userData) {
        const user = JSON.parse(userData);
        const updatedUser = {
          ...user,
          username: updatedProfile.username,
          email: updatedProfile.email
        };
        localStorage.setItem('user', JSON.stringify(updatedUser));
        console.log('Updated user data in localStorage');
      }
    } catch (storageError) {
      console.error('Error updating localStorage:', storageError);
    }

    // Call the onProfileSet callback
    if (onProfileSet) {
      onProfileSet(updatedProfile);
    }

    // Show success step instead of closing immediately
    setStep(2);
    toast.success('Profile updated successfully!');

    // Close after a delay to show success animation
    setTimeout(() => {
      onClose();
    }, 3000);
  };

  // Helper function to handle error response
  const handleErrorResponse = (error) => {
    console.error('Error updating profile:', error);

    // Special case for "Failed to create or update user profile" error
    if (error.message === 'Failed to create or update user profile') {
      setErrors({
        form: 'We were unable to create or update your profile. This could be due to a connection issue with the server. Please check your network connection and try again.'
      });

      // Add a retry button
      setTimeout(() => {
        const errorDiv = document.querySelector('[class*="bg-red-900"]');
        if (errorDiv) {
          // Create retry button if it doesn't exist
          if (!document.getElementById('retry-profile-btn')) {
            const retryBtn = document.createElement('button');
            retryBtn.id = 'retry-profile-btn';
            retryBtn.className = 'mt-2 px-4 py-2 bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 text-white rounded-lg transition-colors text-sm font-medium';
            retryBtn.textContent = 'Try Again';
            retryBtn.onclick = () => {
              // Clear errors and retry
              setErrors({});
              // Submit the form again
              document.querySelector('form').dispatchEvent(new Event('submit', { cancelable: true }));
            };
            errorDiv.appendChild(retryBtn);
          }
        }
      }, 100);

      return;
    }

    // Check if the error has a response with data
    if (error.response && error.response.data) {
      const errorData = error.response.data;
      console.log('Error response data:', errorData);

      // Check for specific error codes
      if (errorData.code === 'USERNAME_TAKEN') {
        setErrors({ username: 'This username is already taken. Please choose another one.' });
      } else if (errorData.code === 'EMAIL_TAKEN') {
        setErrors({ email: 'This email is already in use. Please use a different email.' });
      } else if (errorData.validationErrors) {
        setErrors(errorData.validationErrors);
      } else {
        setErrors({ form: errorData.error || 'Failed to update profile. Please try again.' });
      }
    } else if (error.code === 'USERNAME_TAKEN') {
      setErrors({ username: 'This username is already taken. Please choose another one.' });
    } else if (error.code === 'EMAIL_TAKEN') {
      setErrors({ email: 'This email is already in use. Please use a different email.' });
    } else if (error.status === 500) {
      // Server error
      setErrors({ form: 'Server error. Please try again later or contact support.' });
    } else if (error.status === 404 || error.code === 'USER_NOT_FOUND') {
      // User not found error
      setErrors({
        form: 'User not found in the database. This usually happens when your wallet is not properly connected or registered. Please try reconnecting your wallet.'
      });

      // Add a reconnect button
      setTimeout(() => {
        const errorDiv = document.querySelector('[class*="bg-red-900"]');
        if (errorDiv) {
          // Create reconnect button if it doesn't exist
          if (!document.getElementById('reconnect-wallet-btn')) {
            const reconnectBtn = document.createElement('button');
            reconnectBtn.id = 'reconnect-wallet-btn';
            reconnectBtn.className = 'mt-2 px-4 py-2 bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 text-white rounded-lg transition-colors text-sm font-medium';
            reconnectBtn.textContent = 'Reconnect Wallet';
            reconnectBtn.onclick = async () => {
              try {
                // Clear authentication data
                localStorage.removeItem('token');
                localStorage.removeItem('user');

                // Wait a moment for the cleanup to complete
                await new Promise(resolve => setTimeout(resolve, 500));

                // Reload the page to force re-authentication
                window.location.reload();
              } catch (error) {
                console.error('Error during cleanup:', error);
                // Fallback to direct reload
                window.location.reload();
              }
            };
            errorDiv.appendChild(reconnectBtn);
          }
        }
      }, 100);
    } else if (error.message && error.message.includes('Network')) {
      // Network error
      setErrors({ form: 'Network error. Please check your connection and try again.' });
    } else {
      // Generic error
      setErrors({ form: error.message || 'An unexpected error occurred. Please try again.' });

      // Log detailed error information
      console.error('Detailed error information:', {
        status: error.status,
        message: error.message,
        details: error.details || error.data,
        code: error.code
      });

      // Add a retry button for generic errors
      setTimeout(() => {
        const errorDiv = document.querySelector('[class*="bg-red-900"]');
        if (errorDiv) {
          // Create retry button if it doesn't exist
          if (!document.getElementById('retry-profile-btn')) {
            const retryBtn = document.createElement('button');
            retryBtn.id = 'retry-profile-btn';
            retryBtn.className = 'mt-2 px-4 py-2 bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 text-white rounded-lg transition-colors text-sm font-medium';
            retryBtn.textContent = 'Try Again';
            retryBtn.onclick = () => {
              // Clear errors and retry
              setErrors({});
              // Submit the form again
              document.querySelector('form').dispatchEvent(new Event('submit', { cancelable: true }));
            };
            errorDiv.appendChild(retryBtn);
          }
        }
      }, 100);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50">
      <div className="relative bg-gradient-to-br from-gray-900 to-gray-950 rounded-2xl p-8 w-full max-w-md mx-4 border border-purple-500/20 shadow-xl shadow-purple-500/10">
        {/* Decorative elements */}
        <div className="absolute -top-10 -right-10 w-40 h-40 bg-purple-600/20 rounded-full blur-3xl"></div>
        <div className="absolute -bottom-10 -left-10 w-40 h-40 bg-blue-600/20 rounded-full blur-3xl"></div>

        {step === 1 ? (
          <>
            <div className="relative z-10">
              <div className="w-16 h-16 bg-gradient-to-br from-purple-600 to-blue-600 rounded-2xl flex items-center justify-center mx-auto mb-6 shadow-lg shadow-purple-500/20">
                <FaUser className="text-white text-2xl" />
              </div>

              <h2 className="text-2xl font-bold text-white text-center mb-2">Welcome to BitPlay!</h2>
              <p className="text-gray-300 text-center mb-2">
                Complete your profile to enhance your experience. Your username will be visible to other players.
              </p>
              <p className="text-yellow-400 text-center mb-8 font-medium">
                Setting a username is required to continue.
              </p>

              <form onSubmit={handleSubmit}>
                <div className="mb-6">
                  <label htmlFor="username" className="block text-gray-300 mb-2 font-medium">
                    Username <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <FaUser className="text-gray-400" />
                    </div>
                    <input
                      type="text"
                      id="username"
                      name="username"
                      value={formData.username}
                      onChange={handleChange}
                      className="w-full pl-10 pr-4 py-3 bg-gray-800/50 border border-gray-700 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all"
                      placeholder="Enter a username"
                      autoFocus
                    />
                  </div>
                  {errors.username && (
                    <p className="text-red-500 mt-2 text-sm">{errors.username}</p>
                  )}
                </div>

                <div className="mb-6">
                  <label htmlFor="email" className="block text-gray-300 mb-2 font-medium">
                    Email Address
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <FaEnvelope className="text-gray-400" />
                    </div>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      className="w-full pl-10 pr-4 py-3 bg-gray-800/50 border border-gray-700 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all"
                      placeholder="Enter your email (optional)"
                    />
                  </div>
                  {errors.email && (
                    <p className="text-red-500 mt-2 text-sm">{errors.email}</p>
                  )}
                  <p className="text-gray-400 text-xs mt-1">We'll use this to notify you about important updates.</p>
                </div>

                {errors.form && (
                  <div className="mb-6 p-4 bg-red-900/30 border border-red-700/50 rounded-xl">
                    <p className="text-red-400 text-sm">{errors.form}</p>
                  </div>
                )}

                <div className="flex justify-center mt-8">
                  <button
                    type="submit"
                    className="w-full px-6 py-4 bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 text-white rounded-xl transition-all duration-300 font-medium text-lg flex items-center justify-center shadow-lg shadow-purple-600/20"
                    disabled={isLoading || isRefreshingToken}
                  >
                    {isLoading || isRefreshingToken ? (
                      <svg className="animate-spin -ml-1 mr-2 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                    ) : null}
                    {isRefreshingToken ? "Refreshing Authentication..." :
                     isLoading ? "Saving..." : "Save Profile"}
                  </button>
                </div>
              </form>
            </div>
          </>
        ) : (
          <div className="flex flex-col items-center justify-center py-8 relative z-10">
            <div className="w-20 h-20 bg-gradient-to-br from-green-500 to-teal-500 rounded-full flex items-center justify-center mb-6 animate-bounce-slow shadow-lg shadow-green-500/20">
              <FaCheckCircle className="text-white text-3xl" />
            </div>

            <h2 className="text-2xl font-bold text-white text-center mb-4">Profile Updated!</h2>
            <p className="text-gray-300 text-center mb-8">
              Your profile has been successfully updated. You can now enjoy the full BitPlay experience!
            </p>

            <button
              onClick={onClose}
              className="px-8 py-3 bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 text-white rounded-xl transition-all duration-300 font-medium"
            >
              Continue
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default UsernameSetupModal;
